import { Controller } from "@hotwired/stimulus"
import { getColorClasses, createToken, positionFixedDropdown, toggleClearButton } from "../utils/dropdown_utils"

export default class extends Controller {
  static targets = ["input", "dropdown", "tokenContainer", "optionsContainer", "scrollIndicator", "clearBtn", "search", "searchContainer", "noResults", "select", "option", "sectionGroup"]
  static values = {
    color: { type: String, default: "blue" },
    placeholder: { type: String, default: "Select..." },
    tokenClass: String
  }

  connect() {
    this.repositionHandler = this.repositionIfOpen.bind(this)
  }

  disconnect() {
    this.teardownFixedListeners()
  }

  // Server renders initial UI; JS only handles interactions from here.

  showDropdown() {
    if (this._suppressNextOpen) {
      this._suppressNextOpen = false
      return
    }
    const hasAvailable = Array.from(this.selectTarget.options).some((opt) => !opt.selected)
    if (!hasAvailable) return

    this.dropdownTarget.classList.remove("hidden")
    this.dropdownTarget.classList.add("flex")
    this.inputTarget.setAttribute("aria-expanded", "true")
    this.updateSearchVisibility()
    positionFixedDropdown(this.dropdownTarget, this.tokenContainerTarget)
    this.setupFixedListeners()

    if (this.hasSearchContainerTarget && !this.searchContainerTarget.classList.contains("hidden")) {
      this.searchTarget.value = ""
      this.resetFilter()
      this.updateScrollIndicator()
      requestAnimationFrame(() => this.searchTarget.focus())
    } else {
      this.inputTarget.focus()
      this.updateScrollIndicator()
    }
  }

  hideDropdown() {
    this.clearOptionFocus()
    this.inputTarget.setAttribute("aria-expanded", "false")
    this.inputTarget.removeAttribute("aria-activedescendant")
    this.dropdownTarget.classList.add("hidden")
    this.dropdownTarget.classList.remove("flex")
    this.teardownFixedListeners()
  }

  setupFixedListeners() {
    window.addEventListener("resize", this.repositionHandler)
    window.addEventListener("scroll", this.repositionHandler, true)
  }

  teardownFixedListeners() {
    if (!this.repositionHandler) return
    window.removeEventListener("resize", this.repositionHandler)
    window.removeEventListener("scroll", this.repositionHandler, true)
  }

  repositionIfOpen() {
    if (this.dropdownTarget.classList.contains("hidden")) return
    positionFixedDropdown(this.dropdownTarget, this.tokenContainerTarget)
  }

  handleKeydown(event) {
    const { key } = event

    if (key === "Escape") {
      event.preventDefault()
      this.hideDropdown()
      this._suppressNextOpen = true
      this.inputTarget.focus()
      return
    }

    if (key === "ArrowDown" || key === "ArrowUp") {
      event.preventDefault()
      if (this.dropdownTarget.classList.contains("hidden")) {
        this.showDropdown()
        return
      }
      this.moveOptionFocus(key === "ArrowDown" ? 1 : -1)
      return
    }

    if (key === "Enter") {
      event.preventDefault()
      const focused = this.element.querySelector(".ts-option-focused")
      if (focused) focused.click()
      return
    }

    // Backspace on the readonly input removes the last token
    if (key === "Backspace" && event.currentTarget === this.inputTarget) {
      const tokens = this.tokenContainerTarget.querySelectorAll("[data-token-id]")
      if (tokens.length > 0) {
        const removeBtn = tokens[tokens.length - 1].querySelector("[data-action*='removeToken']")
        if (removeBtn) removeBtn.click()
      }
    }
  }

  moveOptionFocus(direction) {
    const options = Array.from(
      this.optionsContainerTarget.querySelectorAll("[data-tokenized-select-target='option']:not(.hidden)")
    )
    if (options.length === 0) return

    const current = this.element.querySelector(".ts-option-focused")
    const currentIndex = current ? options.indexOf(current) : -1

    this.clearOptionFocus()

    const nextIndex = direction > 0
      ? (currentIndex < options.length - 1 ? currentIndex + 1 : 0)
      : (currentIndex > 0 ? currentIndex - 1 : options.length - 1)

    options[nextIndex].classList.add("ts-option-focused")
    options[nextIndex].scrollIntoView({ block: "nearest" })
    if (options[nextIndex].id) {
      this.inputTarget.setAttribute("aria-activedescendant", options[nextIndex].id)
    }
  }

  clearOptionFocus() {
    const focused = this.element.querySelector(".ts-option-focused")
    if (focused) focused.classList.remove("ts-option-focused")
  }

  selectOption(event) {
    event.stopPropagation()
    this.clearOptionFocus()
    const optionEl = event.currentTarget
    const id = optionEl.dataset.id
    const name = optionEl.dataset.name
    const color = optionEl.dataset.color || this.colorValue

    const selectOption = this.selectTarget.querySelector(`option[value="${id}"]`)
    if (selectOption) selectOption.selected = true

    this.renderToken(id, name, color)
    optionEl.remove()
    this.updateSectionGroupVisibility()

    if (this.hasSearchContainerTarget) this.searchTarget.value = ""
    this.inputTarget.value = ""
    if (this.hasNoResultsTarget) this.noResultsTarget.classList.add("hidden")
    this.updateSearchVisibility()
    this.updatePlaceholder()
    toggleClearButton(this.hasClearBtnTarget ? this.clearBtnTarget : null, this.tokenContainerTarget)
    positionFixedDropdown(this.dropdownTarget, this.tokenContainerTarget)

    const hasAvailable = Array.from(this.selectTarget.options).some((opt) => !opt.selected)
    if (!hasAvailable) this.hideDropdown()

    this.dispatch("tokens-changed", { bubbles: true })
  }

  renderToken(id, name, color) {
    const tokenClass = this.hasTokenClassValue ? this.tokenClassValue : null
    const token = createToken(id, name, getColorClasses(color), "click->tokenized-select#removeToken", tokenClass)
    this.tokenContainerTarget.insertBefore(token, this.inputTarget)
  }

  removeToken(event) {
    event.stopPropagation()
    const token = event.currentTarget.closest("[data-token-id]")
    const id = token.dataset.tokenId

    const selectOption = this.selectTarget.querySelector(`option[value="${id}"]`)
    if (selectOption) {
      selectOption.selected = false
      // Clear the "No options available" placeholder before re-inserting the first option.
      const hasNoOptions = this.optionsContainerTarget.querySelectorAll("[data-tokenized-select-target='option']").length === 0
      if (hasNoOptions) this.optionsContainerTarget.replaceChildren()
      this.appendDropdownOption(selectOption)
    }

    token.remove()
    toggleClearButton(this.hasClearBtnTarget ? this.clearBtnTarget : null, this.tokenContainerTarget)
    this.updateSectionGroupVisibility()
    this.updateSearchVisibility()
    this.updateScrollIndicator()
    this.updatePlaceholder()
    if (!this.dropdownTarget.classList.contains("hidden")) {
      positionFixedDropdown(this.dropdownTarget, this.tokenContainerTarget)
    }
    this.dispatch("tokens-changed", { bubbles: true })
  }

  appendDropdownOption(selectOption) {
    const div = document.createElement("div")
    div.id = `tokenized-select-option-${selectOption.value}`
    div.className = "pl-6 pr-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
    div.setAttribute("role", "option")
    div.setAttribute("aria-selected", "false")
    div.dataset.tokenizedSelectTarget = "option"
    div.dataset.id = selectOption.value
    div.dataset.name = selectOption.textContent.trim()
    div.dataset.color = selectOption.dataset.color || this.colorValue
    div.dataset.action = "click->tokenized-select#selectOption"
    div.textContent = selectOption.textContent.trim()

    if (this.hasSectionGroupTarget) {
      // Re-insert into the matching section group, alphabetically within that group.
      const sectionName = selectOption.dataset.section || ""
      const group = this.sectionGroupTargets.find((g) => {
        const heading = g.querySelector(".text-xs.font-semibold")
        return (heading ? heading.textContent.trim() : "") === sectionName
      }) || this.sectionGroupTargets[this.sectionGroupTargets.length - 1]

      this.insertOptionAlphabetically(group, div)
      return
    }

    this.insertOptionAlphabetically(this.optionsContainerTarget, div)
  }

  insertOptionAlphabetically(container, el) {
    const name = (el.dataset.name || el.textContent.trim()).toLowerCase()
    const insertBefore = Array.from(
      container.querySelectorAll("[data-tokenized-select-target='option']")
    ).find((opt) => (opt.dataset.name || opt.textContent.trim()).toLowerCase() > name)
    insertBefore ? container.insertBefore(el, insertBefore) : container.appendChild(el)
  }

  clearAll(event) {
    event.stopPropagation()

    // Only clear the container when it holds the "No options available" placeholder
    // (i.e. no option targets are present). Otherwise the unselected options that are
    // still in the dropdown would be wiped and only the formerly-selected ones would
    // come back, leaving the user with a truncated list.
    const hasNoOptions = this.optionsContainerTarget.querySelectorAll("[data-tokenized-select-target='option']").length === 0
    if (hasNoOptions) this.optionsContainerTarget.replaceChildren()

    Array.from(this.selectTarget.options).forEach((opt) => {
      if (opt.selected) {
        opt.selected = false
        this.appendDropdownOption(opt)
      }
    })

    this.tokenContainerTarget.querySelectorAll("[data-token-id]").forEach((t) => t.remove())
    this.updateSectionGroupVisibility()
    toggleClearButton(this.hasClearBtnTarget ? this.clearBtnTarget : null, this.tokenContainerTarget)
    this.updatePlaceholder()
    this.hideDropdown()
    this.dispatch("tokens-changed", { bubbles: true })
  }

  filterOptions() {
    const query = this.searchTarget.value.toLowerCase().trim()

    this.optionTargets.forEach((option) => {
      const text = (option.dataset.name || option.textContent).toLowerCase()
      const matches = query === "" || text.includes(query)
      option.classList.toggle("hidden", !matches)
      this.renderHighlightedText(option, option.dataset.name || option.textContent.trim(), query)
    })

    if (this.hasNoResultsTarget) {
      const anyVisible = this.optionTargets.some((opt) => !opt.classList.contains("hidden"))
      this.noResultsTarget.classList.toggle("hidden", anyVisible)
    }

    this.updateSectionGroupVisibility()
    this.updateScrollIndicator()
    this.clearOptionFocus()
    this.inputTarget.removeAttribute("aria-activedescendant")
  }

  resetFilter() {
    this.optionTargets.forEach((option) => {
      option.classList.remove("hidden")
      this.renderHighlightedText(option, option.dataset.name || option.textContent.trim(), "")
    })
    if (this.hasNoResultsTarget) this.noResultsTarget.classList.add("hidden")
    this.updateSectionGroupVisibility()
  }

  updateSectionGroupVisibility() {
    if (!this.hasSectionGroupTarget) return
    this.sectionGroupTargets.forEach((group) => {
      const options = group.querySelectorAll("[data-tokenized-select-target='option']")
      const hasVisible = Array.from(options).some((opt) => !opt.classList.contains("hidden"))
      group.classList.toggle("hidden", !hasVisible)
    })
  }

  updateScrollIndicator() {
    if (!this.hasScrollIndicatorTarget) return
    const visibleCount = Array.from(
      this.optionsContainerTarget.querySelectorAll("[data-tokenized-select-target='option']")
    ).filter((opt) => !opt.classList.contains("hidden")).length
    this.scrollIndicatorTarget.classList.toggle("hidden", visibleCount <= 6)
    this.optionsContainerTarget.scrollTop = 0
  }

  updateSearchVisibility() {
    if (!this.hasSearchContainerTarget) return
    const totalAvailable = Array.from(this.selectTarget.options).filter((opt) => !opt.selected).length
    this.searchContainerTarget.classList.toggle("hidden", totalAvailable <= 6)
  }

  updatePlaceholder() {
    const hasTokens = this.tokenContainerTarget.querySelectorAll("[data-token-id]").length > 0
    const hasAvailable = Array.from(this.selectTarget.options).some((opt) => !opt.selected)

    if (!hasAvailable) {
      this.inputTarget.placeholder = ""
      this.inputTarget.classList.add("hidden")
    } else {
      this.inputTarget.classList.remove("hidden")
      this.inputTarget.placeholder = hasTokens ? "Add more..." : this.placeholderValue
    }
  }

  renderHighlightedText(option, rawName, query) {
    option.replaceChildren()
    if (!query) {
      option.appendChild(document.createTextNode(rawName))
      return
    }
    const regex = new RegExp(`(${this.escapeRegExp(query)})`, "ig")
    rawName.split(regex).forEach((part) => {
      if (part.toLowerCase() === query.toLowerCase()) {
        const mark = document.createElement("span")
        mark.className = "bg-blue-100 text-blue-900 rounded-sm"
        mark.textContent = part
        option.appendChild(mark)
      } else {
        option.appendChild(document.createTextNode(part))
      }
    })
  }

  escapeRegExp(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hideDropdown()
      if (this.hasSearchContainerTarget) this.searchTarget.value = ""
      this.inputTarget.value = ""
      this.resetFilter()
    }
  }
}
